import React from "react";

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
} from "@mui/material";
import { LicenseClaim } from "./Claim";
import { useMutation, useQuery } from "@apollo/client";
import { CLAIM_LICENSE, GET_LICENSE, LicenseResult } from "../../Queries";

interface ComponentProps {
  jwt?: string;
  children?: React.ReactNode;
}

/** Payment modes: IN = institution, ST = student code, IO = inclusive opt-in, OO = inclusive opt-out */
const INCLUSIVE_OPTIN = "IO";
const INCLUSIVE_OPTOUT = "OO";

const LicenseGate: React.FC<ComponentProps> = ({ jwt, children }) => {
  const { loading: roleLoading, data: roleData } = useQuery<LicenseResult>(
    GET_LICENSE,
    {
      context: {
        headers: {
          Authorization: `JWT ${jwt}`,
        },
      },
    },
  );

  const [claimLicense, { loading: claimPending, error: claimError }] =
    useMutation(CLAIM_LICENSE, {
      refetchQueries: ["MyLicense"],
    });

  if (roleLoading) {
    return <div>Loading...</div>;
  }

  const isLicensed = !roleData?.myLicense.includes("unlicensed");
  const paymentMode = roleData?.paymentMode ?? null;
  const isInclusiveOptIn = paymentMode === INCLUSIVE_OPTIN;
  const isInclusiveOptOut = paymentMode === INCLUSIVE_OPTOUT;

  const errorMessage = claimError && claimError.message;

  async function handleClaim(tokenString: string) {
    await claimLicense({
      variables: {
        tokenString,
      },
      context: {
        headers: {
          Authorization: `JWT ${jwt}`,
        },
      },
    });
  }

  if (isLicensed) {
    return <>{children}</>;
  }

  // Inclusive access modes: show message only (no code entry)
  if (isInclusiveOptIn) {
    return (
      <Dialog open>
        <DialogTitle>Access not yet available</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are not on the inclusive access list for this course. Please opt
            in via the bookstore. After the opt-in period, your instructor will
            update access. If you have already opted in, contact your
            instructor.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button disabled>Continue</Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (isInclusiveOptOut) {
    return (
      <Dialog open>
        <DialogTitle>Access not available</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have opted out of inclusive access for this course. If this is
            an error, please contact your instructor or the bookstore to update
            your status.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button disabled>Continue</Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Student code mode: show code entry
  return (
    <LicenseClaim
      onClaim={handleClaim}
      isPending={claimPending}
      errorMessage={errorMessage}
    />
  );
};

export default LicenseGate;
